import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";

import {
  createCompanyFinanceAllocation,
  createCompanyFinanceCorrection,
  createCompanyFinanceObligation,
  createCompanyFinanceReceipt,
  createCompanyFinanceSettlement,
  fetchCompanyFinanceAllocations,
  fetchCompanyFinanceCorrections,
  fetchCompanyFinanceObligations,
  fetchCompanyFinanceReceipts,
  fetchCompanyFinanceSettlements,
} from "../api/companyFinance";
import { fetchCompany, fetchMembers, fetchMyCompany } from "../api/companyWorkspace";
import {
  canAccessCompanyFinanceBooks,
  canCreateCompanyObligation,
  canMutateCompanyFinance,
  counterpartyKindLabel,
  formatCompanyFinanceDate,
  formatCompanyFinanceDelta,
  formatCompanyFinanceMoney,
  groupRowsByCurrency,
  listResults,
  mapCompanyFinanceError,
  membershipGenerationOf,
  obligationStatusLabel,
  correctionTargetKindLabel,
} from "../lib/companyFinanceLabels";
import { companyStatusLabel } from "../lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../lib/companyFocusRevalidation";
import CompanyPageHeader from "../ui/CompanyPageHeader";
import {
  CreateAllocationDialog,
  CreateCorrectionDialog,
  CreateObligationDialog,
  CreateReceiptDialog,
  CreateSettlementDialog,
} from "./companyFinanceDialogs";

function amountsDiffer(a, b) {
  if (a == null || b == null) return false;
  return String(a) !== String(b);
}

function ReceiptCard({ row }) {
  const showEffective = amountsDiffer(row.amount, row.effective_amount);
  return (
    <article
      className="feature-company-finance__record"
      data-testid={`company-finance-receipt-${row.id}`}
    >
      <div className="feature-company-finance__record-main">
        <h3 className="feature-company-finance__record-title">
          Надходження #{row.id}
        </h3>
        <p className="feature-company-finance__record-meta">
          {formatCompanyFinanceDate(row.received_on)}
          {row.deal_reference ? ` · ${row.deal_reference}` : ""}
        </p>
        {row.note ? (
          <p className="feature-company-finance__note">{row.note}</p>
        ) : null}
      </div>
      <dl className="feature-company-finance__money-grid">
        <div>
          <dt>{showEffective ? "Початково" : "Сума"}</dt>
          <dd>{formatCompanyFinanceMoney(row.amount, row.currency)}</dd>
        </div>
        {showEffective ? (
          <div>
            <dt>Після коригувань</dt>
            <dd>{formatCompanyFinanceMoney(row.effective_amount, row.currency)}</dd>
          </div>
        ) : null}
        <div>
          <dt>Розподілено</dt>
          <dd>{formatCompanyFinanceMoney(row.allocated_amount, row.currency)}</dd>
        </div>
        <div>
          <dt>Вільно</dt>
          <dd>{formatCompanyFinanceMoney(row.unallocated_amount, row.currency)}</dd>
        </div>
      </dl>
    </article>
  );
}

function ObligationCard({ row }) {
  const showEffective = amountsDiffer(row.amount, row.effective_amount);
  const partial =
    row.status === "open" &&
    Number(row.settled_amount) > 0 &&
    Number(row.outstanding) > 0;
  return (
    <article
      className={[
        "feature-company-finance__record",
        partial ? "feature-company-finance__record--partial" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid={`company-finance-obligation-${row.id}`}
      data-status={row.status}
      data-partial={partial ? "true" : "false"}
    >
      <div className="feature-company-finance__record-main">
        <h3 className="feature-company-finance__record-title">
          Зобовʼязання #{row.id}
        </h3>
        <p className="feature-company-finance__record-meta">
          {counterpartyKindLabel(row.counterparty_kind)}
          {row.member?.display_name ? ` · ${row.member.display_name}` : ""}
          {` · ${obligationStatusLabel(row.status)}`}
          {row.due_on ? ` · до ${formatCompanyFinanceDate(row.due_on)}` : ""}
          {row.deal_reference ? ` · ${row.deal_reference}` : ""}
        </p>
      </div>
      <dl className="feature-company-finance__money-grid">
        <div>
          <dt>{showEffective ? "Початково" : "Сума"}</dt>
          <dd>{formatCompanyFinanceMoney(row.amount, row.currency)}</dd>
        </div>
        {showEffective ? (
          <div>
            <dt>Після коригувань</dt>
            <dd>{formatCompanyFinanceMoney(row.effective_amount, row.currency)}</dd>
          </div>
        ) : null}
        <div>
          <dt>Профінансовано</dt>
          <dd>{formatCompanyFinanceMoney(row.funded_amount, row.currency)}</dd>
        </div>
        <div>
          <dt>Сплачено</dt>
          <dd>{formatCompanyFinanceMoney(row.settled_amount, row.currency)}</dd>
        </div>
        <div>
          <dt>Залишок</dt>
          <dd>{formatCompanyFinanceMoney(row.outstanding, row.currency)}</dd>
        </div>
      </dl>
      {partial ? (
        <p className="feature-company-finance__partial-note" role="status">
          Часткова виплата: зобовʼязання ще відкрите.
        </p>
      ) : null}
    </article>
  );
}

function AllocationCard({ row }) {
  return (
    <article
      className="feature-company-finance__record"
      data-testid={`company-finance-allocation-${row.id}`}
    >
      <div className="feature-company-finance__record-main">
        <h3 className="feature-company-finance__record-title">
          Розподіл / резерв #{row.id}
        </h3>
        <p className="feature-company-finance__record-meta">
          Надходження #{row.receipt_id} → зобовʼязання #{row.obligation_id}
        </p>
        <p className="feature-company-finance__earmark-note">
          Профінансовано (резерв коштів). Це не виплата.
        </p>
      </div>
      <dl className="feature-company-finance__money-grid">
        <div>
          <dt>Сума резерву</dt>
          <dd>{formatCompanyFinanceMoney(row.amount, row.currency)}</dd>
        </div>
        {amountsDiffer(row.amount, row.effective_amount) ? (
          <div>
            <dt>Після коригувань</dt>
            <dd>{formatCompanyFinanceMoney(row.effective_amount, row.currency)}</dd>
          </div>
        ) : null}
      </dl>
    </article>
  );
}

function SettlementCard({ row }) {
  return (
    <article
      className="feature-company-finance__record"
      data-testid={`company-finance-settlement-${row.id}`}
    >
      <div className="feature-company-finance__record-main">
        <h3 className="feature-company-finance__record-title">
          Виплата #{row.id}
        </h3>
        <p className="feature-company-finance__record-meta">
          Зобовʼязання #{row.obligation_id}
          {row.settled_on
            ? ` · ${formatCompanyFinanceDate(row.settled_on)}`
            : ""}
        </p>
      </div>
      <dl className="feature-company-finance__money-grid">
        <div>
          <dt>Сплачено</dt>
          <dd>{formatCompanyFinanceMoney(row.amount, row.currency)}</dd>
        </div>
        {amountsDiffer(row.amount, row.effective_amount) ? (
          <div>
            <dt>Після коригувань</dt>
            <dd>{formatCompanyFinanceMoney(row.effective_amount, row.currency)}</dd>
          </div>
        ) : null}
      </dl>
    </article>
  );
}

function CorrectionCard({ row }) {
  return (
    <article
      className="feature-company-finance__record"
      data-testid={`company-finance-correction-${row.id}`}
    >
      <div className="feature-company-finance__record-main">
        <h3 className="feature-company-finance__record-title">
          Коригування #{row.id}
        </h3>
        <p className="feature-company-finance__record-meta">
          {correctionTargetKindLabel(row.target_kind)} #{row.target_id}
        </p>
        <p className="feature-company-finance__note">{row.reason}</p>
      </div>
      <dl className="feature-company-finance__money-grid">
        <div>
          <dt>Дельта</dt>
          <dd>{formatCompanyFinanceDelta(row.delta_amount, row.currency)}</dd>
        </div>
      </dl>
    </article>
  );
}

function Section({
  id,
  title,
  action,
  empty,
  children,
  currencyGroups,
}) {
  return (
    <section
      className="feature-company-finance__section"
      aria-labelledby={id}
      data-testid={`company-finance-section-${id}`}
    >
      <div className="feature-company-finance__section-head">
        <h2 id={id}>{title}</h2>
        {action}
      </div>
      {currencyGroups && currencyGroups.length > 1 ? (
        <p className="feature-company-finance__currency-note" role="note">
          Суми з різних валют не обʼєднуються в один підсумок.
        </p>
      ) : null}
      {children}
      {empty}
    </section>
  );
}

function CompanyFinanceContent() {
  const { companyId } = useParams();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const mutationBusyRef = useRef(false);

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [members, setMembers] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [obligations, setObligations] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  const [mutationBusy, setMutationBusy] = useState(false);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [bannerError, setBannerError] = useState(null);

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [obligationOpen, setObligationOpen] = useState(false);
  const [allocationOpen, setAllocationOpen] = useState(false);
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);

  const load = useCallback(({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return Promise.resolve();
    }
    const keepVisible = background && stateRef.current === "ready";
    const op = scopeGate.beginRead({
      companyId,
      actorId,
    });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
      setBannerError(null);
    }

    return Promise.all([
      fetchCompany(companyId),
      fetchMyCompany(),
    ])
      .then(([companyData, mine]) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return null;
        }
        const mineMatches =
          mine?.company?.id != null &&
          sameCompanyId(mine.company.id, companyId)
            ? mine.membership
            : null;
        const effectiveMembership =
          mineMatches || companyData?.my_membership || null;
        if (!canAccessCompanyFinanceBooks(companyData, effectiveMembership)) {
          setCompany(companyData);
          setMembership(effectiveMembership);
          setAuthorityActorId(actorId);
          setState("forbidden");
          return null;
        }
        return Promise.all([
          Promise.resolve(companyData),
          Promise.resolve(effectiveMembership),
          fetchCompanyFinanceReceipts(companyId),
          fetchCompanyFinanceObligations(companyId),
          fetchCompanyFinanceAllocations(companyId),
          fetchCompanyFinanceSettlements(companyId),
          fetchCompanyFinanceCorrections(companyId),
          fetchMembers(companyId, "active").catch(() => []),
        ]);
      })
      .then((bundle) => {
        if (!bundle) return;
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const [
          companyData,
          effectiveMembership,
          receiptsPayload,
          obligationsPayload,
          allocationsPayload,
          settlementsPayload,
          correctionsPayload,
          membersPayload,
        ] = bundle;
        setCompany(companyData);
        setMembership(effectiveMembership);
        setAuthorityActorId(actorId);
        setReceipts(listResults(receiptsPayload));
        setObligations(listResults(obligationsPayload));
        setAllocations(listResults(allocationsPayload));
        setSettlements(listResults(settlementsPayload));
        setCorrections(listResults(correctionsPayload));
        setMembers(listResults(membersPayload));
        revalidationConfirmed = true;
        setBannerError((current) =>
          current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
        );
        setState("ready");
      })
      .catch((err) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const mapped = mapCompanyFinanceError(err);
        const status = err?.response?.status;
        const authorityLoss =
          status === 404 ||
          status === 403 ||
          mapped.code === "not_company_record" ||
          mapped.code === "finance_permission_required";
        if (keepVisible && !authorityLoss) {
          setBannerError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
          return;
        }
        setCompany(null);
        setMembership(null);
        setAuthorityActorId(null);
        if (err?.response?.status === 404 || mapped.code === "not_company_record") {
          setState("not-found");
          return;
        }
        if (
          err?.response?.status === 403 ||
          mapped.code === "finance_permission_required"
        ) {
          setError(mapped.message);
          setState("forbidden");
          return;
        }
        setError(mapped.message || messageFromError(err));
        setState("error");
      })
      .finally(() => {
        settleRevalidationWrites({
          keepVisible: keepVisible,
          confirmed: revalidationConfirmed,
          token: backgroundToken,
          tokenAtStart,
          tokenRef: revalidationTokenRef,
          writesSuspendedRef,
          setWritesSuspended,
        });
      });
  }, [actorId, companyId, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({
      companyId,
      actorId,
      membershipGeneration: membershipGenerationOf(null, null),
      bindMembershipGeneration: true,
    });
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    // Scope change must not leave a prior dialog open against a new identity.
    setReceiptOpen(false);
    setObligationOpen(false);
    setAllocationOpen(false);
    setSettlementOpen(false);
    setCorrectionOpen(false);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
    };
  }, [companyId, actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Do not rebind generation from a previous route's company while the new
    // companyId/actorId load is in flight — that bumps lifetime and leaves
    // the screen stuck in loading (same-mounted route transitions).
    if (!company || !sameCompanyId(company.id, companyId)) return;
    if (
      authorityActorId != null &&
      !sameActorId(authorityActorId, actorId)
    ) {
      return;
    }
    const generation = membershipGenerationOf(company, membership);
    scopeGate.bindIdentity({
      companyId,
      actorId,
      membershipGeneration: generation,
      bindMembershipGeneration: true,
    });
  }, [company, membership, companyId, actorId, authorityActorId, scopeGate]);

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: mutationBusy,
    onRevalidate: () => load({ background: true }),
  });

  const runMutation = async (mutateFn) => {
    if (mutationBusyRef.current || writesSuspendedRef.current) {
      return { accepted: false, duplicateBlocked: true };
    }
    mutationBusyRef.current = true;
    setMutationBusy(true);
    const generation = membershipGenerationOf(company, membership);
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      membershipGeneration: generation,
    });
    try {
      const result = await mutateFn();
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return { accepted: false, stale: true };
      }
      const refreshed = await refreshAfterMutation(op);
      if (!refreshed) {
        return { accepted: false, stale: true };
      }
      return { accepted: true, result };
    } catch (error) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        // Stale rejection: silent — no banner, no refetch, no throw into dialog.
        return { accepted: false, stale: true, error };
      }
      const mapped = mapCompanyFinanceError(error);
      if (mapped.refetch) {
        // Current-scope 409: refetch only while mutation ownership remains valid.
        if (scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          load();
        }
      }
      if (
        mapped.message &&
        scopeGate.mutationStillValid(op, { mounted: mountedRef.current })
      ) {
        setBannerError(mapped.message);
      }
      throw error;
    } finally {
      mutationBusyRef.current = false;
      setMutationBusy(false);
    }
  };

  /**
   * Authoritative post-mutation refresh owned by the mutation operation.
   * Returns true only when state updates were applied under still-valid scope.
   */
  const refreshAfterMutation = async (mutationOp) => {
    if (!scopeGate.mutationStillValid(mutationOp, { mounted: mountedRef.current })) {
      return false;
    }
    const bound = scopeGate.getBoundIdentity();
    const refreshCompanyId = bound.companyId;
    if (refreshCompanyId == null) {
      return false;
    }
    try {
      const [
        receiptsPayload,
        obligationsPayload,
        allocationsPayload,
        settlementsPayload,
        correctionsPayload,
      ] = await Promise.all([
        fetchCompanyFinanceReceipts(refreshCompanyId),
        fetchCompanyFinanceObligations(refreshCompanyId),
        fetchCompanyFinanceAllocations(refreshCompanyId),
        fetchCompanyFinanceSettlements(refreshCompanyId),
        fetchCompanyFinanceCorrections(refreshCompanyId),
      ]);
      if (!scopeGate.mutationStillValid(mutationOp, { mounted: mountedRef.current })) {
        return false;
      }
      setReceipts(listResults(receiptsPayload));
      setObligations(listResults(obligationsPayload));
      setAllocations(listResults(allocationsPayload));
      setSettlements(listResults(settlementsPayload));
      setCorrections(listResults(correctionsPayload));
      setBannerError(null);
      return true;
    } catch (error) {
      if (!scopeGate.mutationStillValid(mutationOp, { mounted: mountedRef.current })) {
        return false;
      }
      const mapped = mapCompanyFinanceError(error);
      setBannerError(mapped.message || messageFromError(error));
      return false;
    }
  };

  if (state === "loading") {
    return (
      <div className="feature-company-workspace__state" data-testid="company-finance-loading">
        <Spinner size="lg" label="Завантаження фінансів компанії" />
      </div>
    );
  }

  if (state === "not-found") {
    return (
      <div className="feature-company-workspace__state" data-testid="company-finance-not-found">
        <EmptyState
          icon="fa-solid fa-magnifying-glass"
          title="Запис недоступний"
          description="Фінансові дані цієї компанії недоступні."
          size="md"
        />
        <Link to="/companies/mine" className="action-btn">
          Моя компанія
        </Link>
      </div>
    );
  }

  if (state === "forbidden") {
    return (
      <div className="feature-company-workspace__state" data-testid="company-finance-forbidden">
        <EmptyState
          icon="fa-solid fa-lock"
          title="Немає доступу"
          description={
            error || "У вас немає доступу до фінансів компанії."
          }
          size="md"
        />
        <Link to={`/companies/${companyId}`} className="action-btn">
          Профіль компанії
        </Link>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="feature-company-workspace__state" data-testid="company-finance-error">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Помилка завантаження"
          description={error || "Спробуйте оновити сторінку."}
          size="md"
        />
        <button type="button" className="action-btn" onClick={load}>
          Повторити
        </button>
      </div>
    );
  }

  const companyMatchesRoute =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  if (!companyMatchesRoute || !actorMatches) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження фінансів компанії" />
      </div>
    );
  }

  const canMutate = canMutateCompanyFinance(company, membership);
  const canCreateObligation = canCreateCompanyObligation(company, membership);
  const archived = company.status === "archived";

  return (
    <section
      className="clients-page feature-company-workspace feature-company-finance"
      id="Csection"
      tabIndex="-1"
      data-testid="company-finance-screen"
    >
      <div className="container">
        <CompanyPageHeader
          title="Фінанси компанії"
          subtitle="Корпоративна книга надходжень, зобовʼязань і виплат"
          company={company}
          membership={membership}
          companyId={company.id}
          activeNav="finance"
        />

        {archived ? (
          <div className="feature-company-workspace__banner" role="status">
            Компанія в архіві ({companyStatusLabel(company.status)}). Доступні
            продовження книг: надходження, розподіл, виплати та коригування.
            Нові зобовʼязання недоступні.
          </div>
        ) : null}

        {bannerError ? (
          <div className="feature-company-finance__banner-error" role="alert">
            {bannerError}
          </div>
        ) : null}

        <Section
          id="receipts"
          title="Надходження"
          currencyGroups={groupRowsByCurrency(receipts)}
          action={
            canMutate ? (
              <button
                type="button"
                className="action-btn"
                data-testid="company-finance-open-receipt"
                disabled={writesSuspended}
                onClick={() => {
                  if (writesSuspended) return;
                  setReceiptOpen(true);
                }}
              >
                Додати надходження
              </button>
            ) : null
          }
          empty={
            receipts.length === 0 ? (
              <EmptyState
                icon="fa-solid fa-inbox"
                title="Немає надходжень"
                description="Коли компанія отримає кошти, записи зʼявляться тут."
                size="sm"
              />
            ) : null
          }
        >
          <div className="feature-company-finance__list">
            {receipts.map((row) => (
              <ReceiptCard key={row.id} row={row} />
            ))}
          </div>
        </Section>

        <Section
          id="obligations"
          title="Зобовʼязання"
          currencyGroups={groupRowsByCurrency(obligations)}
          action={
            canCreateObligation ? (
              <button
                type="button"
                className="action-btn"
                data-testid="company-finance-open-obligation"
                disabled={writesSuspended}
                onClick={() => {
                  if (writesSuspended) return;
                  setObligationOpen(true);
                }}
              >
                Додати зобовʼязання
              </button>
            ) : null
          }
          empty={
            obligations.length === 0 ? (
              <EmptyState
                icon="fa-solid fa-file-invoice-dollar"
                title="Немає зобовʼязань"
                description="Зобовʼязання перед співробітниками, партнерами або зовнішніми сторонами зʼявляться тут."
                size="sm"
              />
            ) : null
          }
        >
          <div className="feature-company-finance__list">
            {obligations.map((row) => (
              <ObligationCard key={row.id} row={row} />
            ))}
          </div>
        </Section>

        <Section
          id="allocations"
          title="Розподіли / резерв коштів"
          currencyGroups={groupRowsByCurrency(allocations)}
          action={
            canMutate ? (
              <button
                type="button"
                className="action-btn"
                data-testid="company-finance-open-allocation"
                disabled={writesSuspended}
                onClick={() => {
                  if (writesSuspended) return;
                  setAllocationOpen(true);
                }}
              >
                Зарезервувати кошти
              </button>
            ) : null
          }
          empty={
            allocations.length === 0 ? (
              <EmptyState
                icon="fa-solid fa-layer-group"
                title="Немає розподілів"
                description="Резерв коштів з надходження під зобовʼязання відображається окремо від виплат."
                size="sm"
              />
            ) : null
          }
        >
          <div className="feature-company-finance__list">
            {allocations.map((row) => (
              <AllocationCard key={row.id} row={row} />
            ))}
          </div>
        </Section>

        <Section
          id="settlements"
          title="Виплати"
          currencyGroups={groupRowsByCurrency(settlements)}
          action={
            canMutate ? (
              <button
                type="button"
                className="action-btn"
                data-testid="company-finance-open-settlement"
                disabled={writesSuspended}
                onClick={() => {
                  if (writesSuspended) return;
                  setSettlementOpen(true);
                }}
              >
                Зафіксувати виплату
              </button>
            ) : null
          }
          empty={
            settlements.length === 0 ? (
              <EmptyState
                icon="fa-solid fa-money-bill-transfer"
                title="Немає виплат"
                description="Фактичні виплати зʼявляться тут після фіксації."
                size="sm"
              />
            ) : null
          }
        >
          <div className="feature-company-finance__list">
            {settlements.map((row) => (
              <SettlementCard key={row.id} row={row} />
            ))}
          </div>
        </Section>

        <Section
          id="corrections"
          title="Коригування"
          currencyGroups={groupRowsByCurrency(corrections)}
          action={
            canMutate ? (
              <button
                type="button"
                className="action-btn"
                data-testid="company-finance-open-correction"
                disabled={writesSuspended}
                onClick={() => {
                  if (writesSuspended) return;
                  setCorrectionOpen(true);
                }}
              >
                Додати коригування
              </button>
            ) : null
          }
          empty={
            corrections.length === 0 ? (
              <EmptyState
                icon="fa-solid fa-clock-rotate-left"
                title="Немає коригувань"
                description="Коригування зберігаються як окремі записи історії."
                size="sm"
              />
            ) : null
          }
        >
          <div className="feature-company-finance__list">
            {corrections.map((row) => (
              <CorrectionCard key={row.id} row={row} />
            ))}
          </div>
        </Section>
      </div>

      <CreateReceiptDialog
        open={receiptOpen}
        writesSuspended={writesSuspended}
        onClose={() => setReceiptOpen(false)}
        onSubmit={async (body) =>
          runMutation(() => createCompanyFinanceReceipt(companyId, body))
        }
      />
      <CreateObligationDialog
        open={obligationOpen}
        writesSuspended={writesSuspended}
        onClose={() => setObligationOpen(false)}
        members={members}
        onSubmit={async (body) =>
          runMutation(() => createCompanyFinanceObligation(companyId, body))
        }
      />
      <CreateAllocationDialog
        open={allocationOpen}
        writesSuspended={writesSuspended}
        onClose={() => setAllocationOpen(false)}
        receipts={receipts}
        obligations={obligations}
        onSubmit={async (body) =>
          runMutation(() => createCompanyFinanceAllocation(companyId, body))
        }
      />
      <CreateSettlementDialog
        open={settlementOpen}
        writesSuspended={writesSuspended}
        onClose={() => setSettlementOpen(false)}
        obligations={obligations}
        onSubmit={async (body) =>
          runMutation(() => createCompanyFinanceSettlement(companyId, body))
        }
      />
      <CreateCorrectionDialog
        open={correctionOpen}
        writesSuspended={writesSuspended}
        onClose={() => setCorrectionOpen(false)}
        receipts={receipts}
        obligations={obligations}
        allocations={allocations}
        settlements={settlements}
        onSubmit={async (body) =>
          runMutation(() => createCompanyFinanceCorrection(companyId, body))
        }
      />
    </section>
  );
}

export default CompanyFinanceContent;
